import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, RotateCcw } from "lucide-react";

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["strong", "b", "em", "i", "br", "p", "span", "u"],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true,
};

/**
 * Lightweight WYSIWYG editor.
 * Stores plain text or simple HTML (<strong>, <em>, <br>).
 * Uses contentEditable + execCommand for bold / italic.
 * Returns the HTML string via onChange.
 */
export default function RichEditor({ value, onChange, placeholder, testId }) {
  const ref = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false });

  // Sync external value into editor only when it really differs.
  // DOMPurify guards against any unsafe HTML even though value comes from our own CMS.
  useEffect(() => {
    if (ref.current) {
      const clean = DOMPurify.sanitize(value || "", PURIFY_CONFIG);
      if (clean !== ref.current.innerHTML) {
        ref.current.innerHTML = clean;
      }
    }
  }, [value]);

  const updateActive = () => {
    setActive({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
    });
  };

  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
    updateActive();
  };

  const onInput = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const onPaste = (e) => {
    // Strip rich formatting on paste — keep only plain text
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <div className="border border-gray-300 rounded overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        <ToolBtn onClick={() => exec("bold")} active={active.bold} title="Grassetto (Ctrl+B)">
          <Bold className="w-3.5 h-3.5" strokeWidth={2} />
        </ToolBtn>
        <ToolBtn onClick={() => exec("italic")} active={active.italic} title="Corsivo (Ctrl+I)">
          <Italic className="w-3.5 h-3.5" strokeWidth={2} />
        </ToolBtn>
        <span className="mx-1 text-gray-300">|</span>
        <ToolBtn onClick={() => exec("removeFormat")} title="Rimuovi formattazione">
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
        </ToolBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        onKeyUp={updateActive}
        onMouseUp={updateActive}
        className="px-3 py-2 min-h-[64px] text-sm font-sans focus:outline-none whitespace-pre-wrap"
        style={{ wordBreak: "break-word" }}
        data-placeholder={placeholder}
      />
    </div>
  );
}

function ToolBtn({ children, active, onClick, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`px-2 py-1 rounded text-sm transition-colors ${
        active ? "bg-[#8B3A3A] text-white" : "text-[#2C2C2A] hover:bg-gray-200"
      }`}
    >
      {children}
    </button>
  );
}
